import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getDashboardStats, getRecentActivity } from '../services/assetService';
import { DashboardStats, Asset } from '../types/asset';
import { StatusBadge } from '../components/shared/StatusBadge';
import { FileTypeBadge } from '../components/shared/FileTypeBadge';
import { formatRelativeTime, extractFileName } from '../utils/formatters';
import { resources } from '../resources';
import { ChevronRight, Database, Calendar, Activity, AlertCircle } from 'lucide-react';

export const Dashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [recent, setRecent] = useState<Asset[]>([]);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      const [s, r] = await Promise.all([getDashboardStats(), getRecentActivity(8)]);
      setStats(s);
      setRecent(r);
    };
    fetchData();
  }, []);

  if (!stats) return <div className="animate-pulse">{resources.common.loading}</div>;

  const statCards = [
    { label: resources.dashboard.stats.totalAssets, value: stats.totalAssets, icon: Database, color: 'text-primary', bg: 'bg-primary/10' },
    { label: resources.dashboard.stats.receivedToday, value: stats.receivedToday, icon: Calendar, color: 'text-secondary', bg: 'bg-secondary/10' },
    { label: resources.dashboard.stats.activeProcessing, value: stats.activeProcessing, icon: Activity, color: 'text-primary', bg: 'bg-primary/10' },
    { label: resources.dashboard.stats.failedAssets, value: stats.failedAssets, icon: AlertCircle, color: 'text-destructive', bg: 'bg-destructive/10' },
  ];

  return (
    <div className="space-y-8">
      <header>
        <h1 className="text-2xl font-bold text-foreground">{resources.dashboard.title}</h1>
        <p className="text-muted-foreground">{resources.dashboard.subtitle}</p>
      </header>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((card, idx) => (
          <div key={idx} className="bg-card p-6 rounded-2xl border border-border shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between mb-4">
              <div className={`${card.bg} p-2 rounded-lg`}>
                <card.icon className={`w-5 h-5 ${card.color}`} />
              </div>
            </div>
            <div className="text-3xl font-bold text-foreground tabular-nums">
              {card.value.toLocaleString()}
            </div>
            <p className="text-sm font-medium text-muted-foreground mt-1">{card.label}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Activity Feed */}
        <div className="lg:col-span-2 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
              {resources.dashboard.activity.title}
              <span className="bg-secondary/10 text-secondary text-[10px] px-1.5 py-0.5 rounded-full animate-pulse uppercase tracking-wider font-bold">{resources.common.live}</span>
            </h2>
            <button onClick={() => navigate('/assets')} className="text-sm text-primary font-medium hover:underline">
              {resources.dashboard.activity.viewAll}
            </button>
          </div>

          <div className="bg-card border border-border rounded-2xl overflow-hidden shadow-sm">
            {recent.map((asset) => (
              <div
                key={asset.id}
                onClick={() => navigate(`/assets/${asset.id}`)}
                className="flex items-center justify-between p-4 hover:bg-muted border-b border-border last:border-0 cursor-pointer transition-colors group"
              >
                <div className="flex items-center gap-4 min-w-0">
                  <div className="w-10 h-10 bg-muted rounded-xl flex items-center justify-center shrink-0">
                    <Database className="w-5 h-5 text-muted-foreground" />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                       <span className="font-medium text-foreground truncate">{extractFileName(asset.locationPath)}</span>
                       <FileTypeBadge type={asset.fileType} />
                    </div>
                    <div className="text-xs text-muted-foreground flex items-center gap-2 mt-1">
                      <span className="font-mono text-muted-foreground">{asset.id.slice(0, 8)}</span>
                      <span>&bull;</span>
                      <span>{formatRelativeTime(asset.receivedOn)}</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <StatusBadge status={asset.state} size="sm" />
                  <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-foreground transition-all" />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Quick Filters */}
        <div className="space-y-8">
           <div className="bg-card border border-border rounded-2xl p-6 shadow-sm">
             <h3 className="font-semibold text-foreground mb-4">{resources.dashboard.quickFilters.title}</h3>
             <div className="space-y-2">
               {[
                 { label: resources.dashboard.quickFilters.today, color: 'text-foreground bg-muted', path: '/assets?date=today' },
                 { label: resources.dashboard.quickFilters.thisWeek, color: 'text-foreground bg-muted', path: '/assets?date=week' },
                 { label: resources.dashboard.quickFilters.errors, color: 'text-destructive bg-destructive/10', path: '/assets?state=ERROR' },
                 { label: resources.dashboard.quickFilters.queue, color: 'text-primary bg-primary/10', path: '/assets?state=INITIAL' },
               ].map((btn, i) => (
                 <button
                  key={i}
                  className={`w-full text-left px-4 py-3 rounded-xl text-sm font-medium hover:brightness-95 transition-all ${btn.color}`}
                 >
                   {btn.label}
                 </button>
               ))}
             </div>
           </div>
        </div>
      </div>
    </div>
  );
};
